import { useState, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Radio,
  Sparkles,
  User,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Send,
  Instagram,
  ShoppingBag,
  Bot,
  Store,
  Settings,
} from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-media-query";

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: { label: string; path: string; icon: React.ComponentType<{ className?: string }> }[];
}

const navItems: NavItem[] = [
  {
    label: "Home",
    path: "/home",
    icon: LayoutDashboard,
  },
  {
    label: "Canais",
    path: "/canais",
    icon: Radio,
    children: [
      { label: "WhatsApp", path: "/canais?platform=whatsapp", icon: MessageCircle },
      { label: "Telegram", path: "/canais?platform=telegram", icon: Send },
      { label: "Instagram", path: "/canais?platform=instagram", icon: Instagram },
    ],
  },
  {
    label: "Copilot",
    path: "/copilot",
    icon: Sparkles,
  },
  {
    label: "Perfil",
    path: "/perfil",
    icon: User,
    children: [
      { label: "Catalogo", path: "/perfil?tab=catalogo", icon: ShoppingBag },
      { label: "Agente AI", path: "/perfil?tab=agente", icon: Bot },
      { label: "Marketplaces", path: "/perfil?tab=marketplaces", icon: Store },
      { label: "Config", path: "/perfil?tab=config", icon: Settings },
    ],
  },
];

const mobileNavItems = [
  { label: "Home", path: "/home", icon: LayoutDashboard },
  { label: "Canais", path: "/canais", icon: Radio },
  { label: "Copilot", path: "/copilot", icon: Sparkles },
  { label: "Perfil", path: "/perfil", icon: User },
];

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

export function Sidebar({ collapsed, onCollapse }: SidebarProps) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = useCallback((label: string) => {
    setExpandedSections((prev) =>
      prev.includes(label)
        ? prev.filter((l) => l !== label)
        : [...prev, label]
    );
  }, []);

  const isActive = (path: string) => {
    const basePath = path.split("?")[0]!;
    return location.pathname === basePath || location.pathname.startsWith(basePath + "/");
  };

  if (isMobile) {
    return (
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/95 backdrop-blur-xl safe-bottom">
        <div className="flex items-center justify-around px-2 py-1">
          {mobileNavItems.map((item) => {
            const active = isActive(item.path);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={cn(
                  "flex flex-col items-center gap-0.5 px-3 py-2 text-[10px] font-medium transition-colors min-w-[44px] min-h-[44px] justify-center rounded-xl",
                  active
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <item.icon
                  className={cn("h-5 w-5", active && "text-primary")}
                />
                <span>{item.label}</span>
                {active && (
                  <motion.div
                    layoutId="mobile-nav-indicator"
                    className="absolute bottom-0 h-0.5 w-8 rounded-full bg-primary"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    );
  }

  return (
    <motion.aside
      initial={false}
      animate={{ width: collapsed ? 64 : 240 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      className="fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-border bg-sidebar"
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between px-4">
        <Logo collapsed={collapsed} />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onCollapse(!collapsed)}
          className={cn(
            "h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground",
            collapsed && "mx-auto"
          )}
          aria-label={collapsed ? "Expandir sidebar" : "Recolher sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-4" role="navigation">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const active = isActive(item.path);
            const expanded = expandedSections.includes(item.label);
            const hasChildren = item.children && item.children.length > 0;

            const linkContent = (
              <div
                className={cn(
                  "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-sidebar-foreground hover:bg-accent hover:text-foreground"
                )}
              >
                {active && (
                  <motion.div
                    layoutId="sidebar-active"
                    className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full bg-primary"
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
                <item.icon className={cn("h-5 w-5 shrink-0", active && "text-primary")} />
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {hasChildren && (
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 text-muted-foreground transition-transform duration-200",
                          expanded && "rotate-180"
                        )}
                      />
                    )}
                  </>
                )}
              </div>
            );

            if (collapsed) {
              return (
                <li key={item.label}>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <NavLink to={item.path}>{linkContent}</NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="font-medium">
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                </li>
              );
            }

            return (
              <li key={item.label}>
                {hasChildren ? (
                  <>
                    <button
                      onClick={() => toggleSection(item.label)}
                      className="w-full"
                      aria-expanded={expanded}
                    >
                      {linkContent}
                    </button>
                    <AnimatePresence initial={false}>
                      {expanded && (
                        <motion.ul
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          {item.children!.map((child) => (
                            <li key={child.label}>
                              <NavLink
                                to={child.path}
                                className={cn(
                                  "flex items-center gap-3 rounded-lg py-2 pl-11 pr-3 text-sm text-sidebar-foreground transition-colors hover:bg-accent hover:text-foreground"
                                )}
                              >
                                <child.icon className="h-4 w-4" />
                                <span>{child.label}</span>
                              </NavLink>
                            </li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <NavLink to={item.path}>{linkContent}</NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom section */}
      {!collapsed && (
        <div className="border-t border-border p-4">
          <div className="rounded-lg bg-gradient-to-r from-primary/10 to-indigo-500/10 p-3">
            <p className="text-xs font-medium text-foreground">Plano Pro</p>
            <p className="mt-0.5 text-[10px] text-muted-foreground">
              1.240 / 5.000 dispatches
            </p>
            <div className="mt-2 h-1.5 rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-indigo-400"
                style={{ width: "24.8%" }}
              />
            </div>
          </div>
        </div>
      )}
    </motion.aside>
  );
}
